import React from "react";
import ButtonGroups, { BGItem } from "components/ButtonGroups";
import { REFERENCE_UNITS } from "features/MappingEditor/consts";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { COEStore } from "../ComponentEditorStore";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import AddedChild from "./AddedChild";
import useReorderList from "hooks/useReorderList";
import Button from "components/Button";
import { IconPlus } from "@tabler/icons-react";
import ProductSearch from "features/ProductSearch";
import Alert from "components/Alert";
import useFeatureFlag from "hooks/useFeatureFlag";
import ProductSearchModal from "features/AdvancedProductSearch/ProductSearchModal";

const ReferenceUnitAndProducts = () => {
  const { t } = useTranslation();
  const [showReferenceUnitAlert, setShowReferenceUnitAlert] =
    React.useState(false);
  const [updateThickness, setUpdateThickness] = React.useState(false);
  const { isFeatureVisible } = useFeatureFlag();

  React.useEffect(() => {
    COEStore.data.childrenList.length === 0 && setShowReferenceUnitAlert(false);
    COEStore.fetchComponentInsights();
  }, [COEStore.data.childrenList?.length]);

  const onChangeReferenceUnit = (value: BGItem | undefined) => {
    COEStore.onChangeData("reference_unit", value);
    if (COEStore.data.childrenList.length) {
      const hasUnitConversion = COEStore.data.childrenList.some(
        (item) => item.unit_conversion
      );
      setShowReferenceUnitAlert(hasUnitConversion);
    }
    setUpdateThickness(true);
    value?.id === "M2" && COEStore.fetchComponentInsights();
  };

  const currentReferenceUnit = COEStore.data.reference_unit
    ? REFERENCE_UNITS.find(
        (item) => item.id === COEStore.data.reference_unit.id
      )
    : REFERENCE_UNITS[2];

  const hasAnyProductSelected = COEStore.data.childrenList.length;

  const {
    moveDown,
    moveUp,
    items: reorderedList,
  } = useReorderList(COEStore.data.childrenList);

  const sortableItems = COEStore.data.childrenList.map((row, index) => ({
    ...row,
    sortableId: `${row.id}-${index}`,
  }));

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  React.useEffect(() => {
    COEStore.setData({
      ...COEStore.data,
      childrenList: reorderedList,
    });
  }, [reorderedList]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = sortableItems.findIndex(
        (item) => item.sortableId === active.id
      );
      const newIndex = sortableItems.findIndex(
        (item) => item.sortableId === over?.id
      );

      const reorderedItems = arrayMove(
        COEStore.data.childrenList,
        oldIndex,
        newIndex
      );

      COEStore.setData({
        ...COEStore.data,
        childrenList: reorderedItems,
      });
    }
  };

  const onClickAdd = () => {
    COEStore.setOpenedSearchInCE(true);
  };

  return (
    <div className="pb-6">
      <div className="text-base font-semibold mb-3 flex items-center">
        {t("components.refrenceUnitAndConstructionPRsCs")}
      </div>
      <ButtonGroups
        items={REFERENCE_UNITS}
        onChange={onChangeReferenceUnit}
        current={currentReferenceUnit}
        containerClassName="w-full mb-3"
        label={t("components.reference_unit")}
        required
        optionClsssName="h-[33px]"
        size="middle"
        helper={t("productEditor.referenceUnitHelper")}
      />
      {showReferenceUnitAlert && (
        <Alert
          type="attention"
          description={t("components.refUnitAlert")}
          className="!bg-transparent border-none px-0 pt-0 pb-3"
          iconClassName="flex items-center"
        />
      )}
      {hasAnyProductSelected ? (
        <div className="flex flex-col items-center justify-center">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableItems.map((item) => item.sortableId)}
            >
              {COEStore.data.childrenList.map((item, index) => (
                <AddedChild
                  childItem={item}
                  key={index}
                  rowIndex={index}
                  moveDown={moveDown}
                  moveUp={moveUp}
                  sortableId={
                    sortableItems.find((_, inx) => inx === index)?.sortableId
                  }
                  referenceUnitChanged={() => setShowReferenceUnitAlert(false)}
                  updateThickness={updateThickness}
                  setUpdateThickness={setUpdateThickness}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      ) : null}
      <Button
        type="secondary"
        width="fit-container"
        leadingIcon={<IconPlus />}
        onClick={onClickAdd}
      >
        {t("components.addProductComponent")}
      </Button>

      {isFeatureVisible("new_product_search") ? (
        <ProductSearchModal
          open={COEStore.openedSearchInCE}
          close={() => COEStore.setOpenedSearchInCE(false)}
        />
      ) : (
        <ProductSearch
          open={COEStore.openedSearchInCE}
          close={() => COEStore.setOpenedSearchInCE(false)}
        />
      )}
    </div>
  );
};

export default observer(ReferenceUnitAndProducts);
